import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'gatsby-plugin-intl'
import moment from 'moment'
import { CircularProgress } from '@material-ui/core'
import { useQuery } from '@tanstack/react-query'
import Checkbox from 'src/components/atoms/CheckboxInput'
import Select from 'src/components/atoms/SelectNative'
import Alert from 'src/components/Alert'
import TextInputMasked from 'src/components/atoms/TextInputMasked'
import DatePicker from 'src/components/atoms/DatePicker'
import ManagePrivateRoomsButton from 'src/features/offers/components/ManagePrivateRoomsButton'
import { BOOLEAN } from 'src/ducks/consts'
import { breakpoint } from 'src/theme/grid'
import LocalStorageService from 'src/services/LocalStorageService'
import { useRoomsSummary } from 'src/features/exchangeRooms/hooks/useExchangeRooms'
import ExchangeRoomsService from 'src/services/ExchangeRoomsService'

const hourOptions = [
  { value: '5', id: '1', label: '5m' },
  { value: '15', id: '2', label: '15m' },
  { value: '30', id: '3', label: '30m' },
  { value: '45', id: '4', label: '45m' },
  { value: '60', id: '5', label: '60m' },
  { value: '90', id: '6', label: '90m' },
  { value: '120', id: '7', label: '2h' },
  { value: '180', id: '8', label: '3h' },
  { value: '360', id: '9', label: '6h' },
  { value: '720', id: '10', label: '12h' },
  { value: '1440', id: '11', label: '24h' }
]

const getStoredMinuteOption = exchangeType => {
  try {
    const value = LocalStorageService.get(
      `last-used-go-public-at-minute-addon-${exchangeType}`
    )
    return hourOptions.some(option => option.value === value) ? value : null
  } catch (error) {
    return null
  }
}

const getTimeStringFromMoment = momentDateTime => momentDateTime.format('HH:mm')
const getDateStringFromMoment = momentDateTime =>
  momentDateTime.format('YYYY-MM-DD')

const useDefaultExchangeRoom = exchangeType =>
  useQuery({
    queryKey: ['exchangeRoomsDefault', exchangeType],
    queryFn: () => ExchangeRoomsService.getDefaultRoom(exchangeType),
    retry: false,
    staleTime: 60 * 1000
  })

const PrivateOfferCreateOptions = ({
  exchangeType,
  onGoPublicDateTimeChange,
  rooms: selectedRooms,
  canCreatePublicOffer
}) => {
  const intl = useIntl()
  const [minutes, setMinutes] = useState('')
  const [avoidGoingPublic, setAvoidGoingPublic] = useState(
    !canCreatePublicOffer
  )
  const [goPublicAtDateTime, setGoPublicAtDateTime] = useState(() =>
    moment().add(15, 'minutes')
  )

  const roomsQuery = useRoomsSummary(exchangeType)
  const defaultRoomQuery = useDefaultExchangeRoom(exchangeType)

  const availableRooms = roomsQuery.data?.data || []
  const defaultRoom = defaultRoomQuery.data?.data || null
  const firstRoomId = availableRooms[0]?.id || null
  const selectedRoomId =
    Array.isArray(selectedRooms) && selectedRooms.length > 0
      ? selectedRooms[0]
      : ''

  const roomOptions = useMemo(
    () =>
      availableRooms.map(room => ({
        id: room.id,
        value: room.id,
        label: room.isDefault
          ? `${room.name} (${intl.formatMessage({
              id: 'forms.privateOfferRoomDefaultBadge'
            })})`
          : room.name
      })),
    [availableRooms, intl]
  )

  useEffect(() => {
    const goPublicAtDateTimeMinutesAddon = getStoredMinuteOption(exchangeType)

    if (goPublicAtDateTimeMinutesAddon) {
      setGoPublicAtDateTime(
        moment().add(goPublicAtDateTimeMinutesAddon, 'minutes')
      )
      setMinutes(goPublicAtDateTimeMinutesAddon)
    } else {
      setGoPublicAtDateTime(moment().add(15, 'minutes'))
      setMinutes('')
    }
  }, [exchangeType])

  useEffect(() => {
    if (selectedRoomId) {
      return
    }

    if (defaultRoom?.id) {
      onGoPublicDateTimeChange('rooms', [defaultRoom.id])
      return
    }

    if (
      !defaultRoomQuery.isLoading &&
      availableRooms.length === 1 &&
      firstRoomId
    ) {
      onGoPublicDateTimeChange('rooms', [firstRoomId])
    }
  }, [
    defaultRoom?.id,
    defaultRoomQuery.isLoading,
    firstRoomId,
    onGoPublicDateTimeChange,
    selectedRoomId,
    availableRooms.length
  ])

  useEffect(() => {
    if (!canCreatePublicOffer) {
      setAvoidGoingPublic(true)
      onGoPublicDateTimeChange('goPublicAt', null)
    }
  }, [canCreatePublicOffer, onGoPublicDateTimeChange])

  const handleMinutesChange = (name, value) => {
    setMinutes(value)
    try {
      LocalStorageService.set(
        `last-used-go-public-at-minute-addon-${exchangeType}`,
        value
      )
    } catch (error) {
      // The form still works when browser storage is unavailable.
    }
    setGoPublicAtDateTime(moment().add(value, 'minutes'))
  }

  const handleAvoidGoingPublicChange = () => {
    if (!canCreatePublicOffer) {
      return
    }

    setAvoidGoingPublic(prevValue => !prevValue)
  }

  const handleRoomsChange = (name, value) => {
    onGoPublicDateTimeChange(name, value ? [value] : null)
  }

  const handleGoPublicAtTimeChange = (name, value) => {
    setMinutes('')
    setGoPublicAtDateTime(
      moment(
        `${getDateStringFromMoment(goPublicAtDateTime)} ${value}`,
        'YYYY-MM-DD HH:mm'
      )
    )
  }

  const handleGoPublicAtDateChange = (name, value) => {
    setMinutes('')
    setGoPublicAtDateTime(
      moment(
        `${value} ${getTimeStringFromMoment(goPublicAtDateTime)}`,
        'YYYY-MM-DD HH:mm'
      )
    )
  }

  useEffect(() => {
    if (avoidGoingPublic) {
      onGoPublicDateTimeChange('goPublicAt', null)
      return
    }

    onGoPublicDateTimeChange('goPublicAt', goPublicAtDateTime.toISOString())
  }, [avoidGoingPublic, goPublicAtDateTime, onGoPublicDateTimeChange])

  const validateGoPublicAtDateTime = () =>
    avoidGoingPublic || goPublicAtDateTime.isAfter(moment())

  const showRoomsLoader = roomsQuery.isLoading || defaultRoomQuery.isLoading
  const hasRooms = roomOptions.length > 0

  let roomField = null

  if (showRoomsLoader) {
    roomField = (
      <RoomLoadingWrapper>
        <FieldLabel>
          {intl.formatMessage({
            id: 'forms.privateOfferRoomLabel'
          })}
        </FieldLabel>
        <LoadingWrapper>
          <CircularProgress size={18} />
          <span>
            {intl.formatMessage({ id: 'forms.privateOfferRoomLoading' })}
          </span>
        </LoadingWrapper>
      </RoomLoadingWrapper>
    )
  } else if (hasRooms) {
    roomField = (
      <Select
        required
        id='rooms'
        name='rooms'
        label={intl.formatMessage({
          id: 'forms.privateOfferRoomLabel'
        })}
        value={selectedRoomId}
        options={roomOptions}
        onChange={handleRoomsChange}
        placeholder={intl.formatMessage({
          id: 'forms.privateOfferRoomPlaceholder'
        })}
      />
    )
  }

  return (
    <Wrapper>
      {showRoomsLoader || hasRooms ? (
        <>
          <RoomSelectionRow>
            <RoomFieldWrapper>{roomField}</RoomFieldWrapper>

            <ManageRoomsAction>
              <ManagePrivateRoomsButton exchangeType={exchangeType} />
            </ManageRoomsAction>
          </RoomSelectionRow>

          <GoPublicOptions>
            {!avoidGoingPublic && (
              <InputsRowWrapper>
                <InputWrapper>
                  <DatePicker
                    label={intl.formatMessage({
                      id: 'forms.privateOfferCreateOptionsGoPublicAtDateLabel'
                    })}
                    disablePast
                    required={false}
                    name='GoPublicAtDate'
                    placeholder='dd.mm.rrrr'
                    value={getDateStringFromMoment(goPublicAtDateTime)}
                    onChange={handleGoPublicAtDateChange}
                    maxDate={moment().add(6, 'days')}
                  />
                </InputWrapper>
                <InputTimeWrapper>
                  <TextInputMasked
                    mask='99:99'
                    label={intl.formatMessage({
                      id: 'forms.privateOfferCreateOptionsGoPublicAtTimeLabel'
                    })}
                    required={false}
                    id='GoPublicAtTime'
                    name='GoPublicAtTime'
                    placeholder='gg:mm'
                    value={getTimeStringFromMoment(goPublicAtDateTime)}
                    onChange={handleGoPublicAtTimeChange}
                    validate={validateGoPublicAtDateTime}
                    type='time'
                    step={300}
                  />
                </InputTimeWrapper>
                <OrWrapper>
                  <p>
                    {intl.formatMessage({
                      id: 'forms.privateOfferCreateOptionsGoPublicAtInOrLabel'
                    })}
                  </p>
                </OrWrapper>
                <HourInputWrapper>
                  <Select
                    required={false}
                    label={intl.formatMessage({
                      id: 'forms.privateOfferCreateOptionsGoPublicAtInLabel'
                    })}
                    options={hourOptions}
                    onChange={handleMinutesChange}
                    name='minutes'
                    value={minutes}
                    placeholder='-'
                  />
                </HourInputWrapper>
              </InputsRowWrapper>
            )}

            <AutoPublishCheckbox
              name='pub'
              value={avoidGoingPublic}
              disabled={!canCreatePublicOffer}
              option={{
                value: BOOLEAN.YES,
                text: intl.formatMessage({
                  id: 'forms.privateOfferCreateOptionsAvoidGoingPublicLabel'
                })
              }}
              onChange={handleAvoidGoingPublicChange}
            />
          </GoPublicOptions>
        </>
      ) : (
        <>
          <FullWidthAlert
            type='warning'
            message={intl.formatMessage({
              id: 'forms.privateOfferRoomNoRooms'
            })}
          />

          <NoRoomsActions>
            <ManagePrivateRoomsButton exchangeType={exchangeType} />
          </NoRoomsActions>

          <GoPublicOptions>
            <AutoPublishCheckbox
              name='pub'
              value={avoidGoingPublic}
              disabled={!canCreatePublicOffer}
              option={{
                value: BOOLEAN.YES,
                text: intl.formatMessage({
                  id: 'forms.privateOfferCreateOptionsAvoidGoingPublicLabel'
                })
              }}
              onChange={handleAvoidGoingPublicChange}
            />
          </GoPublicOptions>
        </>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  margin-top: 0.75rem;
`

const InputsRowWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`

const RoomSelectionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
`

const RoomFieldWrapper = styled.div`
  width: 100%;

  & > * {
    margin-bottom: 0;
  }

  ${breakpoint.m`
    width: 220px;
  `}
`

const GoPublicOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.9rem;
`

const HourInputWrapper = styled.div`
  width: 15%;

  ${breakpoint.m`
    width: 75px;
  `}
`

const InputTimeWrapper = styled.div`
  width: calc(30% - 1rem);

  ${breakpoint.m`
    width: 100px;
  `}
`

const OrWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 5%;

  ${breakpoint.m`
    width: 20px;
  `}
`

const InputWrapper = styled.div`
  width: calc(40% - 1rem);

  ${breakpoint.m`
    width: 150px;
  `}
`

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  height: 40px;
  min-height: 40px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.7).hex()};
`

const RoomLoadingWrapper = styled.div`
  width: 100%;
`

const FieldLabel = styled.span`
  display: flex;
  width: 100%;
  color: ${({ theme }) => theme.colors.black.hex()};
  font-weight: 600;
  font-size: 0.75rem;
  margin-bottom: 0.3rem;
  margin-left: 0.2rem;

  ${({ required }) =>
    required &&
    `
      &:after {
        content: '*';
        color: red;
        font-size: 0.75rem;
      }
    `}
`

const ManageRoomsAction = styled.div`
  display: flex;
  align-items: flex-end;
`

const AutoPublishCheckbox = styled(Checkbox)`
  padding: 0.1rem 0 0;
  margin-top: -0.1rem;
`

const NoRoomsActions = styled.div`
  margin: 0.75rem 0 0.25rem;
`

const FullWidthAlert = styled(Alert)`
  width: 100%;
  margin-bottom: 1rem;
`

export default PrivateOfferCreateOptions
